import React, { FC } from 'react';

import cx from 'classnames';

import { Img, LinkOrButton } from 'components/base';

import sanityImgUtil from 'utils/sanityImgUtil';
import sanityImgSrcSetUtil from 'utils/sanityImgSrcSetUtil';

import {
  GeneralModuleColorScheme,
  GeneralModuleColor,
} from 'constants/ColorScheme';
import Language from 'constants/Language';

import { LinkCardWithImageModule as ILinkCardWithImageModule } from 'types';

interface Props {
  module: ILinkCardWithImageModule;
  applyArticleBodyWidthOffset?: boolean;
}

const LinkCardWithImageModule: FC<Props> = ({
  module,
  applyArticleBodyWidthOffset,
}) => {
  const { linkCard, image, desktopImagePosition, moduleOptions } = module;

  const colorScheme =
    GeneralModuleColorScheme[module.moduleOptions.color as GeneralModuleColor];
  const buttonLabel = linkCard.button.label;
  const buttonVariant = linkCard.button.variant;
  const url = linkCard.button.url;
  const titleElement = (title: string) => {
    return (
      title && (
        <p className={`text-lg mb3_75 color-${colorScheme.text}`}>
          <span className={`link-border-bottom-${colorScheme.text}-hidden`}>
            {title}
          </span>
        </p>
      )
    );
  };
  const descriptionElement = (description?: string) => {
    return (
      description && (
        <div className={`mb1_5 color-${colorScheme.text}`}>
          <span className="text-secondary-sm">{description}</span>
        </div>
      )
    );
  };

  return (
    <div
      className={cx(
        `LinkCardWithImageModule flex flex-col sm:flex-row bg-color-${colorScheme.background} color-${colorScheme.text}`,
        {
          'border-top-gray-lighter': moduleOptions.showBorderTop,
          'border-bottom-gray-lighter': moduleOptions.showBorderBottom,
          'sm:flex-row-reverse': desktopImagePosition === 'right',
          'article-inner-text-full-width': applyArticleBodyWidthOffset,
        }
      )}
    >
      <div className="LinkCardWithImageModule__image col-12 sm:col-6">
        <Img
          alt={image.alt}
          className="w100 h100 fit-cover"
          sizes="(maxWidth: 768px) 600px, (maxWidth: 1024px) 700px, 1990px"
          src={sanityImgUtil(image, 600)}
          srcSet={sanityImgSrcSetUtil(image, 600, 700, 1990)}
        />
      </div>
      <div className="LinkCardWithImageModule__card col-12 sm:col-6 flex-1 py3 sm:py4 md:py6 module-content-padding-x">
        {!url && titleElement(linkCard.title)}
        {url && (
          <LinkOrButton
            ariaLabel={Language.t('Global.pageButtonAriaLabel', {
              title: url,
            })}
            to={url}
            className="link-border-bottom-appear-on-hover"
          >
            {titleElement(linkCard.title)}
          </LinkOrButton>
        )}
        {descriptionElement(linkCard.description)}
        {buttonLabel && url && (
          <LinkOrButton
            ariaLabel={Language.t('Global.pageButtonAriaLabel', {
              title: url,
            })}
            className="inline-flex"
            variant={
              buttonVariant === 'primary'
                ? buttonVariant
                : (`hover-${buttonVariant}` as
                    | 'hover-underline-white'
                    | 'hover-underline-black')
            }
            label={buttonLabel}
            to={url}
          />
        )}
      </div>
    </div>
  );
};

export default LinkCardWithImageModule;
